import { googleApi } from '@/api/googleApi'
import { useSearchPlaceStore } from '@/stores/searchPlace'
import { useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'

import { SessionDataGoogle } from '@/composables/useSearchPlaces'

import { PlaceDetails } from '../interfaces/PlaceDetails'

interface Params {
	placeId: string
}

const LARGE_AREA_TYPES = [
	'country',
	'administrative_area_level_1',
	'administrative_area_level_2',
	'administrative_area_level_3',
]

const isLargeArea = (types: string[]): boolean => {
	return types.some((type) => LARGE_AREA_TYPES.includes(type))
}

const findMainCity = async (
	placeName: string
): Promise<PlaceDetails | null> => {
	const searchQueries = [
		`capital de ${placeName}`,
		`ciudad de ${placeName}`,
		`centro de ${placeName}`,
	]

	for (const searchQuery of searchQueries) {
		try {
			const { data } = await googleApi.post<{ places: PlaceDetails[] }>(
				'/places:searchText',
				{
					textQuery: searchQuery,
					includedType: 'locality',
					maxResultCount: 1,
					languageCode: 'es',
				},
				{
					headers: {
						'X-Goog-FieldMask':
							'places.id,places.types,places.location,places.displayName',
					},
				}
			)

			if (data.places && data.places.length > 0) {
				return data.places[0]
			}
		} catch (error) {
			console.log(`Error buscando con query "${searchQuery}":`, error)
		}
	}

	return null
}

const getPlaceDetails = async (
	params: Params,
	sessionData: Pick<SessionDataGoogle, 'restApiValuesSearch'>
) => {
	const { restApiValuesSearch } = sessionData
	try {
		const { data } = await googleApi.get<PlaceDetails>(
			`/places/${params.placeId}`,
			{
				headers: {
					'X-Goog-FieldMask': 'types,location,displayName',
				},
			}
		)

		restApiValuesSearch(true)

		if (data && isLargeArea(data.types)) {
			const mainCity = await findMainCity(data.displayName.text)

			if (mainCity) {
				return {
					...mainCity,
					displayName: {
						...mainCity.displayName,
						text: `${mainCity.displayName.text}, ${data.displayName.text}`,
					},
				}
			}
		}

		return data
	} catch (error) {
		console.log(error)
	}
}

export const usePlaceDetails = () => {
	const { resetApiValuesSearch, setPlaceDetails } = useSearchPlaceStore()
	const { placeSelected, placeDetails } = storeToRefs(useSearchPlaceStore())

	const { data, error, refetch, isFetching } = useQuery(
		['placeDetails'],
		() =>
			getPlaceDetails(
				{
					placeId: placeSelected.value!.placeId,
				},
				{ restApiValuesSearch: resetApiValuesSearch }
			),
		{
			retry: false,
			enabled: false,
		}
	)

	watch(data, (value) => {
		if (value) {
			setPlaceDetails(value)
		}
	})

	return {
		placeSelected,
		error,
		refetch,
		isFetching,
		placeDetails,

		retryDetails: async () => {
			if (placeSelected) return await refetch()
		},
	}
}
